import { useState, useEffect } from "react";
import axios from "axios";
import { DashboardLayout, adminNavItems } from "@/components/DashboardLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
  Save,
  RotateCcw,
  Plus,
  Trash2,
  DollarSign,
  Briefcase,
  AlertCircle,
  FileSignature,
  Languages,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const LANGS = [
  { code: "no", label: "Norsk", flag: "🇳🇴" },
  { code: "en", label: "English", flag: "🇬🇧" },
  { code: "ro", label: "Română", flag: "🇷🇴" },
];

export default function AdminContractSettings() {
  const { token } = useAuth();
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activePos, setActivePos] = useState(null);
  const [activeLang, setActiveLang] = useState("no");

  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    fetchPositions();
  }, []);

  const fetchPositions = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/contract-settings/positions`, authHeaders);
      setPositions(res.data || []);
      if (res.data?.length && !activePos) setActivePos(res.data[0].id);
    } catch (err) {
      toast.error("Kunne ikke laste posisjoner");
    } finally {
      setLoading(false);
    }
  };

  const updatePosField = (posId, field, value) => {
    setPositions((prev) =>
      prev.map((p) => (p.id === posId ? { ...p, [field]: value } : p))
    );
  };

  const updateListField = (posId, field, lang, idx, value) => {
    setPositions((prev) =>
      prev.map((p) => {
        if (p.id !== posId) return p;
        const list = [...(p[field]?.[lang] || [])];
        list[idx] = value;
        return { ...p, [field]: { ...(p[field] || {}), [lang]: list } };
      })
    );
  };

  const addListItem = (posId, field, lang, initialValue = "") => {
    setPositions((prev) =>
      prev.map((p) => {
        if (p.id !== posId) return p;
        const list = [...(p[field]?.[lang] || []), initialValue];
        return { ...p, [field]: { ...(p[field] || {}), [lang]: list } };
      })
    );
  };

  const removeListItem = (posId, field, lang, idx) => {
    setPositions((prev) =>
      prev.map((p) => {
        if (p.id !== posId) return p;
        const list = [...(p[field]?.[lang] || [])];
        list.splice(idx, 1);
        return { ...p, [field]: { ...(p[field] || {}), [lang]: list } };
      })
    );
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        positions: positions.map((p) => ({
          id: p.id,
          label: p.label,
          rate: parseFloat(p.rate),
          order: p.order,
          responsibilities: p.responsibilities,
          limitations: p.limitations,
        })),
      };
      await axios.put(`${API}/api/contract-settings/positions`, payload, authHeaders);
      toast.success("Endringer lagret!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunne ikke lagre");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm("Tilbakestille alle posisjoner til standardverdier? Dette kan ikke angres.")) return;
    try {
      await axios.post(`${API}/api/contract-settings/reset`, {}, authHeaders);
      toast.success("Tilbakestilt til standardverdier");
      fetchPositions();
    } catch (err) {
      toast.error("Kunne ikke tilbakestille");
    }
  };

  const currentPos = positions.find((p) => p.id === activePos);

  return (
    <DashboardLayout navItems={adminNavItems} title="Kontrakt-innstillinger">
      <div className="space-y-6">
        {/* Header */}
        <Card className="bg-gradient-to-r from-[#F7931A]/10 to-[#FFCD00]/5 border-[#F7931A]/30">
          <CardContent className="p-6 flex items-start gap-4">
            <FileSignature className="w-10 h-10 text-[#F7931A] flex-shrink-0" />
            <div className="flex-1">
              <h2 className="text-xl font-bold text-white mb-1">
                Stillingsnivåer & tarifer
              </h2>
              <p className="text-[#94A3B8] text-sm">
                Rediger timesats, ansvar og begrensninger for hver stilling.
                Endringene gjelder alle <strong>nye</strong> kontrakter — eksisterende kontrakter forblir uendret.
              </p>
            </div>
            <div className="flex gap-2 flex-shrink-0">
              <Button
                variant="outline"
                onClick={handleReset}
                className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                data-testid="reset-defaults-btn"
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                Tilbakestill
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving || loading}
                className="bg-[#23F7DD] text-black hover:bg-[#23F7DD]/80 font-semibold"
                data-testid="save-positions-btn"
              >
                <Save className="w-4 h-4 mr-2" />
                {saving ? "Lagrer..." : "Lagre endringer"}
              </Button>
            </div>
          </CardContent>
        </Card>

        {loading ? (
          <div className="text-center py-12">
            <div className="w-8 h-8 border-2 border-[#23F7DD] border-t-transparent rounded-full animate-spin mx-auto" />
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Position list (left rail) */}
            <Card className="bg-[#0A0A0A] border-white/10 lg:col-span-1 h-fit">
              <CardContent className="p-4 space-y-2">
                <p className="text-[#94A3B8] text-xs uppercase tracking-wider mb-3 flex items-center gap-2">
                  <Briefcase className="w-3 h-3" />
                  Stillinger
                </p>
                {positions.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => setActivePos(p.id)}
                    className={`w-full text-left p-3 rounded-lg transition-colors ${
                      activePos === p.id
                        ? "bg-[#F7931A]/20 border border-[#F7931A]/40 text-white"
                        : "bg-white/5 border border-white/10 text-[#94A3B8] hover:bg-white/10"
                    }`}
                    data-testid={`pos-tab-${p.id}`}
                  >
                    <div className="font-semibold text-sm">{p.label}</div>
                    <div className="text-xs text-[#F7931A] mt-1">
                      {p.rate} NOK/t
                    </div>
                  </button>
                ))}
              </CardContent>
            </Card>

            {/* Editor (right) */}
            <div className="lg:col-span-3 space-y-4">
              {currentPos && (
                <>
                  {/* Basic info */}
                  <Card className="bg-[#0A0A0A] border-white/10">
                    <CardContent className="p-6 space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="text-sm text-[#94A3B8] mb-1 block">
                            Tittel / Stillingsnavn
                          </label>
                          <Input
                            value={currentPos.label || ""}
                            onChange={(e) => updatePosField(currentPos.id, "label", e.target.value)}
                            className="bg-black/40 border-white/20 text-white"
                            data-testid="pos-label-input"
                          />
                        </div>
                        <div>
                          <label className="text-sm text-[#94A3B8] mb-1 block flex items-center gap-1">
                            <DollarSign className="w-3 h-3" />
                            Timesats (NOK/time)
                          </label>
                          <Input
                            type="number"
                            value={currentPos.rate || 0}
                            onChange={(e) => updatePosField(currentPos.id, "rate", e.target.value)}
                            className="bg-black/40 border-white/20 text-white font-mono text-lg"
                            data-testid="pos-rate-input"
                          />
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Language tabs for responsibilities + limitations */}
                  <Card className="bg-[#0A0A0A] border-white/10">
                    <CardContent className="p-6">
                      <Tabs value={activeLang} onValueChange={setActiveLang}>
                        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                          <div className="flex items-center gap-2">
                            <Languages className="w-4 h-4 text-[#94A3B8]" />
                            <span className="text-[#94A3B8] text-sm">Språk:</span>
                          </div>
                          <TabsList className="bg-black/40 border border-white/10">
                            {LANGS.map((l) => (
                              <TabsTrigger
                                key={l.code}
                                value={l.code}
                                data-testid={`lang-tab-${l.code}`}
                              >
                                {l.flag} {l.label}
                              </TabsTrigger>
                            ))}
                          </TabsList>
                        </div>

                        {LANGS.map((l) => (
                          <TabsContent key={l.code} value={l.code} className="space-y-6 mt-0">
                            {/* Responsibilities */}
                            <ListEditor
                              title="✅ Ansvar / Responsibilities"
                              items={currentPos.responsibilities?.[l.code] || []}
                              onChange={(idx, val) => updateListField(currentPos.id, "responsibilities", l.code, idx, val)}
                              onAdd={() => addListItem(currentPos.id, "responsibilities", l.code)}
                              onAddHeader={() => addListItem(currentPos.id, "responsibilities", l.code, "**Section Title**")}
                              onRemove={(idx) => removeListItem(currentPos.id, "responsibilities", l.code, idx)}
                              testIdPrefix={`resp-${l.code}`}
                              color="green"
                            />

                            {/* Limitations */}
                            <ListEditor
                              title="⛔ Begrensninger / Limitations"
                              items={currentPos.limitations?.[l.code] || []}
                              onChange={(idx, val) => updateListField(currentPos.id, "limitations", l.code, idx, val)}
                              onAdd={() => addListItem(currentPos.id, "limitations", l.code)}
                              onAddHeader={() => addListItem(currentPos.id, "limitations", l.code, "**Section Title**")}
                              onRemove={(idx) => removeListItem(currentPos.id, "limitations", l.code, idx)}
                              testIdPrefix={`lim-${l.code}`}
                              color="red"
                            />
                          </TabsContent>
                        ))}
                      </Tabs>
                    </CardContent>
                  </Card>

                  {/* Info box about cumulative behavior */}
                  <Card className="bg-[#23F7DD]/5 border-[#23F7DD]/30">
                    <CardContent className="p-4 flex items-start gap-3">
                      <AlertCircle className="w-5 h-5 text-[#23F7DD] flex-shrink-0 mt-0.5" />
                      <div className="text-sm text-[#CBD5E1]">
                        <strong className="text-white">Kumulativ visning:</strong> Når du genererer en kontrakt for{" "}
                        <strong>{currentPos.label}</strong>, kan du valgfritt vise ansvar fra høyere nivåer
                        (som &quot;Carriere path&quot; for ansatte). Bytt på/av når du oppretter kontrakten.
                      </div>
                    </CardContent>
                  </Card>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

const ListEditor = ({ title, items, onChange, onAdd, onAddHeader, onRemove, testIdPrefix, color }) => {
  const colorClasses = {
    green: "border-green-500/20",
    red: "border-red-500/20",
  };
  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-white font-semibold">{title}</h3>
        <Badge className="bg-white/10 text-[#94A3B8]">{items.length}</Badge>
      </div>
      <div className="space-y-2">
        {items.map((item, idx) => {
          const isHeader = /^\*\*.+\*\*$/.test(item || '');
          return (
            <div
              key={idx}
              className={`flex items-start gap-2 p-2 bg-black/40 border rounded-lg ${
                isHeader ? 'border-emerald-500/40 bg-emerald-500/5' : colorClasses[color]
              }`}
            >
              <span className="text-[#64748B] text-xs mt-2 font-mono w-6">{idx + 1}.</span>
              <Textarea
                value={item}
                onChange={(e) => onChange(idx, e.target.value)}
                className={`bg-transparent border-0 text-white text-sm flex-1 min-h-[40px] resize-none focus-visible:ring-1 ${
                  isHeader ? 'font-bold text-emerald-300' : ''
                }`}
                data-testid={`${testIdPrefix}-${idx}`}
              />
              <Button
                size="sm"
                variant="ghost"
                onClick={() => onRemove(idx)}
                className="text-red-400 hover:bg-red-500/10 flex-shrink-0"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          );
        })}
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={onAdd}
            className="flex-1 border-dashed border-white/20 text-[#94A3B8] hover:text-white hover:border-white/40"
            data-testid={`${testIdPrefix}-add`}
          >
            <Plus className="w-4 h-4 mr-2" />
            Legg til punkt
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={onAddHeader}
            className="border-dashed border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/10"
            data-testid={`${testIdPrefix}-add-header`}
            title="Legg til overskrift (**tittel** blir automatisk fet)"
          >
            <Plus className="w-4 h-4 mr-2" />
            Legg til seksjon
          </Button>
        </div>
      </div>
    </div>
  );
};
